import * as React from 'react'
import { connect } from 'react-redux'
import { match } from 'react-router'
import { Action, Dispatch } from 'redux'
import { actions } from 'store/creditCheck'
import { CustomerCreditCheckInitialState, CustomerCreditCheckState } from 'store/creditCheck'
import { RootState } from 'store/rootReducer'

import { formatString, stripFormatting } from 'src/services/string-formatter'
import { CustomerCreditCheckFormFields, CustomerCreditCheckFormFieldsObject, CustomerCreditCheckModel, FieldModel } from 'typings/api-models/creditCheck'
import CreditHeader from './creditHeader'
import CreditCheckForm from './form'
import LoadingPage from './loading'

class CreditCheckPage extends React.Component<CreditCheckProps, StateProps> {
  constructor(props: CreditCheckProps) {
    super(props)
    this.state = { creditCheckState: CustomerCreditCheckInitialState }
    this.handleChange = this.handleChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChangeToName = this.handleChangeToName.bind(this)
    this.handleClearForm = this.handleClearForm.bind(this)
  }

  public componentDidMount(): void {
    this.props.getCustomerInformation(this.props.match.params.guid)
  }

  public componentWillReceiveProps(nextProps: CreditCheckProps): void {
    this.setState({ creditCheckState: nextProps.creditCheckState })
  }

  public handleChange(field: FieldModel<string>, event: React.FormEvent<HTMLInputElement>, isCheckbox?: boolean): void {
    let updatedState = this.state.creditCheckState
    if (isCheckbox) {
      updatedState = this.updateCheckboxInForm(field.name, event.currentTarget.checked, updatedState)
    } else {
      updatedState.fields[field.name].touched = true
      const newValue = formatString(event.currentTarget.value, field.formatter).result
      updatedState.fields[field.name].value = newValue
      updatedState.fields[field.name].valid = this.isTextInputInValidState(newValue, field.required, field.regex)
    }
    updatedState.valid = this.formIsValid(updatedState.fields)
    this.setState({ creditCheckState: updatedState })
  }

  public handleChangeToName(field: FieldModel<string>, event: React.FormEvent<HTMLInputElement>, isCheckbox?: boolean): void {
    let updatedState = this.state.creditCheckState
    updatedState = this.updateCheckboxInForm(field.name, event.currentTarget.checked, updatedState)
    updatedState = this.restoreOriginalNameToState(updatedState)
    updatedState.valid = this.formIsValid(updatedState.fields)
    this.setState({ creditCheckState: updatedState })
  }

  public handleClearForm(event: React.FormEvent<HTMLButtonElement>): void {
    event.preventDefault()
    let updatedState = this.state.creditCheckState
    const ssn = this.state.creditCheckState.fields.ssn
    updatedState.fields = Object.assign({}, updatedState.fields, new CustomerCreditCheckFormFieldsObject())
    updatedState.fields.ssn = { ...ssn, value: '' }
    updatedState = this.restoreOriginalNameToState(updatedState)
    updatedState.valid = this.formIsValid(updatedState.fields)
    this.setState({ creditCheckState: updatedState })
  }

  public handleSubmit(event: React.FormEvent<HTMLButtonElement>): void {
    event.preventDefault()
    if (this.state.creditCheckState.valid) {
      this.props.postCreditCheckForm(
        this.mapFieldsToApi(
          this.state.creditCheckState.fields,
          this.state.creditCheckState.igsCustomerNumber,
          this.state.creditCheckState.customerInvitationGuid
        )
      )
    }
  }

  private updateCheckboxInForm(fieldName: string, isChecked: boolean, currentState: CustomerCreditCheckState): CustomerCreditCheckState {
    currentState.fields[fieldName].valid = this.isCheckboxInValidState(fieldName, isChecked)
    currentState.fields[fieldName].value = isChecked
    return currentState
  }

  private restoreOriginalNameToState(currentState: CustomerCreditCheckState): CustomerCreditCheckState {
    Object.keys(this.state.creditCheckState.originalName).forEach(e => {
      currentState.fields[e].valid = true
      currentState.fields[e].value = this.state.creditCheckState.originalName[e]
    })
    return currentState
  }

  private isCheckboxInValidState(fieldName: string, isChecked: boolean): boolean {
    const requiredField = this.state.creditCheckState.fields[fieldName].required
    return (requiredField && isChecked) || !requiredField
  }

  private isTextInputInValidState(currentValue: string, required: boolean, regex?: string): boolean {
    const validator = new RegExp(regex ? regex : '')
    return validator.test(currentValue) || (currentValue.length === 0 && !required)
  }

  private formIsValid(fields: CustomerCreditCheckFormFields): boolean {
    return !Object.keys(fields)
      .map(e => fields[e].valid)
      .some(x => x === false)
  }

  private mapFieldsToApi(fields: CustomerCreditCheckFormFields, igsCustomerNumber: string, customerInvitationGuid: string): CustomerCreditCheckModel {
    return {
      address: {
        addressLine1: fields.addressLine1.value,
        addressLine2: fields.addressLine2.value,
        city: fields.city.value,
        state: fields.state.value,
        zipCode: fields.zip.value
      },
      consent: fields.consent.value,
      dateOfBirth: fields.birthDate.value,
      firstName: fields.firstName.value,
      lastName: fields.lastName.value,
      legalNameCorrectlyRepresented: fields.legalNameCorrectlyRepresented.value,
      middleInitial: fields.middleInitial.value,
      phoneNumber: stripFormatting(fields.phone.value, fields.phone.formatter),
      referenceNumber: igsCustomerNumber,
      socialSecurityNumber: stripFormatting(fields.ssn.value, fields.ssn.formatter),
      guid: customerInvitationGuid
    }
  }

  public render(): JSX.Element {
    const { creditCheckState } = this.state
    return (
      <div>
        <CreditHeader />
        <div className="row justify-content-md-center">
          {creditCheckState.loaded && (
            <div className="col-12 col-md-10">
              <div className="row">
                <div className="col-12 order-2 col-md-8 order-md-1">
                  {this.props.creditCheckState.displayMessage.length > 0 && (
                    <div className="col-12 text-center">
                      <h5 className="error-text">{this.props.creditCheckState.displayMessage}</h5>
                    </div>
                  )}
                  <CreditCheckForm
                    creditCheckForm={creditCheckState}
                    handleSubmit={this.handleSubmit}
                    handleChange={this.handleChange}
                    handleClearForm={this.handleClearForm}
                    handleChangeToName={this.handleChangeToName}
                  />
                </div>
                <div className="col-12 order-1 col-md-4 order-md-2">
                  <div className="form-section active">
                    <h3 className="form-section-header">Message</h3>
                    <p>Providing the information will execute a soft credit check. All data is transmitted through a secure connection.</p>
                    <p>Please note: If you have moved within the last 60 days, please use your previous address.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!creditCheckState.loaded && <LoadingPage />}
      </div>
    )
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    creditCheckState: state.creditCheck
  }
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    getCustomerInformation: (creditCheckId: string): Action => dispatch(actions.getCustomerInformation.request(creditCheckId)),
    postCreditCheckForm: (form: CustomerCreditCheckModel): Action => dispatch(actions.postCreditCheckForm.request(form))
  }
}

interface StateProps {
  creditCheckState: CustomerCreditCheckState
}

interface DispatchProps {
  getCustomerInformation: (creditCheckId: string) => Action
  postCreditCheckForm: (form: CustomerCreditCheckModel) => Action
}

interface OwnProps {
  match: match<{ guid: string }>
}

export interface CreditCheckProps extends StateProps, DispatchProps, OwnProps {}

export default connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(CreditCheckPage)



// WEBPACK FOOTER //
// ./src/components/creditCheck/CreditCheckPage.tsx